import React, { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Contract } from "interface/contract";
import { ICON_NAMES } from "utils/constant";
import { GrideDateFormatter } from "ui/Date";
import { FieldFull, FieldSplit, FieldRow } from "ui/CardField";
import { formatDate, formatAmount, formatWeight } from "utils/formatters";

interface ContractsMobileListProps {
	items: Contract[];
	loading: boolean;
	onConfirm: (item: Contract) => Promise<void>;
	onOpenPdf: (item: Contract) => void;
	onLoadMore: () => void;
}

const getStatusClassName = (statusId: number) => {
	if (statusId === 5) return "contract-list-card--status-green";
	if (statusId === 4) return "contract-list-card--status-orange";
	return "contract-list-card--status-red";
};

const formatVessel = (item: Contract) => {
	const registration = item.vesselRegistrationId?.trim();
	const vesselName = item.vesselName?.trim();

	if (registration && vesselName) {
		return `${registration} - ${vesselName}`;
	}

	return registration || vesselName || "-";
};

const ContractsMobileList: FC<ContractsMobileListProps> = ({
	items,
	loading,
	onConfirm,
	onOpenPdf,
	onLoadMore,
}) => {
	const { t } = useTranslation();
	const [confirmingIds, setConfirmingIds] = useState<number[]>([]);

	const empty = useMemo(() => !loading && items.length === 0, [items.length, loading]);

	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
		if (remaining < 200) {
			onLoadMore();
		}
	};

	const handleConfirm = (item: Contract) => {
		if (item.showSign !== 1 || confirmingIds.includes(item.id)) {
			return;
		}

		setConfirmingIds((prev) => [...prev, item.id]);
		onConfirm(item).finally(() => {
			setConfirmingIds((prev) => prev.filter((id) => id !== item.id));
		});
	};

	return (
		<div className="contract-list-mobile" onScroll={handleScroll}>
			{empty && <div className="contract-list-mobile-empty">{t("NoRows")}</div>}

			{items.map((item, itemIndex) => {
				const confirming = confirmingIds.includes(item.id);
				const canConfirm = item.showSign === 1;
				const itemKey = `${item.id}-${item.statusId}-${itemIndex}`;

				return (
					<div
						key={itemKey}
						className={`contract-list-card ${getStatusClassName(item.statusId)}`}
					>
						<div className="contract-list-card-header">
							<div className="contract-list-card-note"><strong>{item.id}</strong></div>
							<div className="contract-list-card-date">
								{item.registrationTime ? (
									<GrideDateFormatter
										date={item.registrationTime}
										formatType="dd.MM.yyyy"
										noTd={true}
									/>
								) : (
									"-"
								)}
							</div>
						</div>

						<div className="contract-list-card-body">
							<FieldRow
								items={[
									{ label: t("RegistrationId"), value: item.registrationId || "-" },
									{ label: t("Package"), value: item.packageId || "-" },
									{ label: t("Status"), value: item.status || "-" },
								]}
							/>
							<FieldRow
								items={[
									{ label: t("Buyer"), value: item.buyerName || "-" },
								]}
							/>
							<FieldRow
								items={[
									{ label: t("Vessel"), value: formatVessel(item) },
								]}
							/>
							<FieldRow
								items={[
									{ label: t("Information"), value: item.information || "-" },
								]}
							/>
						</div>

						<div className="contract-list-card-actions">
							{item.statusId !== 5 && (
								<button
									type="button"
									className="contract-list-icon-btn"
									onClick={() => handleConfirm(item)}
									disabled={!canConfirm || confirming}
									title={t("Confirm")}
								>
									<img src={`/icon/${ICON_NAMES.Confirmation}.svg`} alt={t("Confirm")} />
								</button>
							)}
							<button
								type="button"
								className="contract-list-icon-btn"
								onClick={() => onOpenPdf(item)}
								title={t("Download")}
							>
								<img src={`/icon/${ICON_NAMES.Download}.svg`} alt={t("Download")} />
							</button>
						</div>
					</div>
				);
			})}

			{loading && <div className="contract-list-mobile-loading">{t("Loading")}</div>}
		</div>
	);
};

export default ContractsMobileList;